import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { ErrorPage } from "@/pages/ErrorPage"
import { HomeScreen } from "@/pages/Home"
import { isUpgradePendingAfterSignIn, UPGRADE_PATH } from "@/utils/upgradeEntry"
import type React from "react"
import { Navigate } from "react-router"

// Onboarding-exclusive mode: while ONBOARDING_DATA_ROOM is on, the data room
// is the only live module — every other module route (questionnaire, history,
// knowledgebase, Material Compliance, chat, QMS database, notifications,
// notification settings, company info) renders as unavailable. `onboarding`,
// `settings` and the catch-all stay deliberately open; `data-room` has its own
// gate (`RequireDataRoomActive` below). Each wrapped route stays registered so
// it works again the moment the flag flips off, without needing a redeploy.
export const RequireDataRoomInactive: React.FC<{
  children: React.ReactElement
}> = ({ children }) => {
  const onboardingDataRoomEnabled = useFeatureFlag("ONBOARDING_DATA_ROOM")
  return onboardingDataRoomEnabled ? (
    <ErrorPage variant="feature-unavailable" withLayout />
  ) : (
    children
  )
}

// The data room route itself: while ONBOARDING_DATA_ROOM is off, the route
// does not exist for this org — a direct visit (bookmark, shared link, stale
// tab) redirects home instead of rendering the page.
export const RequireDataRoomActive: React.FC<{
  children: React.ReactElement
}> = ({ children }) => {
  const onboardingDataRoomEnabled = useFeatureFlag("ONBOARDING_DATA_ROOM")
  return onboardingDataRoomEnabled ? children : <Navigate to="/" replace />
}

export const IndexRoute: React.FC = () => {
  const onboardingDataRoomEnabled = useFeatureFlag("ONBOARDING_DATA_ROOM")
  // A Google sign-in started on /upgrade comes back here
  if (isUpgradePendingAfterSignIn()) {
    return <Navigate to={UPGRADE_PATH} replace />
  }
  return onboardingDataRoomEnabled ? (
    <Navigate to="/data-room" replace />
  ) : (
    <HomeScreen />
  )
}
